/**
 * PromoCodeField — le champ « Code promo » des écrans de paiement.
 *
 * CE QU'IL AFFICHE EST CE QUI SERA DÉBITÉ. Le nouveau prix ne vient pas
 * d'un calcul du navigateur : il est renvoyé par
 * POST /api/promo-codes/validate, qui lit le montant PLEIN TARIF sur le
 * price Stripe et y applique le coupon. Barrer un prix calculé ici, puis
 * laisser Stripe débiter autre chose, serait exactement la facturation
 * silencieuse qu'Archer refuse.
 *
 * CE COMPOSANT N'ACCORDE RIEN. La validation qu'il déclenche est un
 * aperçu ; ce qui applique réellement le code est la création de la
 * session Stripe, qui refait la MÊME vérification côté serveur
 * (services/promo_codes.resolve_for_plan). Un client qui bricolerait
 * l'état React n'obtient donc aucune remise — au pire un écran qui ment,
 * corrigé par le 400 du checkout.
 *
 * LES MESSAGES D'ERREUR sont choisis d'après un CODE MACHINE
 * (`detail.error`), jamais d'après le texte renvoyé par le serveur — et
 * encore moins d'après celui de Stripe. « Code invalide », « Code
 * expiré », « Ce code ne s'applique pas à cette offre » : le client lit
 * une phrase, pas un identifiant d'API.
 *
 * Copy ×3 dans le fichier, comme PaymentConfirmModal : ce composant sert
 * plusieurs parcours (abonnements, lettre à l'unité) et son texte ne doit
 * pas dépendre du dictionnaire de l'un d'eux.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  fr: {
    label: 'Code promo',
    placeholder: 'Ton code',
    apply: 'Appliquer',
    applying: '…',
    remove: 'Retirer',
    applied: 'Code {C} appliqué',
    was: 'au lieu de',
    errors: {
      promo_unknown: 'Code invalide.',
      promo_inactive: 'Ce code n’est plus actif.',
      promo_expired: 'Ce code a expiré.',
      promo_exhausted: 'Ce code a atteint son nombre maximum d’utilisations.',
      promo_plan_not_eligible: 'Ce code ne s’applique pas à cette offre.',
      promo_not_for_annual: 'Ce code n’est pas valable sur la facturation annuelle.',
      promo_too_many_tries: 'Trop d’essais. Réessaie dans une minute.',
      promo_not_applied: 'Ce code n’a pas pu être appliqué à cette offre.',
      promo_unavailable: 'Vérification impossible pour l’instant. Réessaie dans un instant.',
      generic: 'Vérification impossible pour l’instant. Réessaie dans un instant.',
    },
  },
  nl: {
    label: 'Promocode',
    placeholder: 'Je code',
    apply: 'Toepassen',
    applying: '…',
    remove: 'Verwijderen',
    applied: 'Code {C} toegepast',
    was: 'in plaats van',
    errors: {
      promo_unknown: 'Ongeldige code.',
      promo_inactive: 'Deze code is niet meer actief.',
      promo_expired: 'Deze code is vervallen.',
      promo_exhausted: 'Deze code heeft zijn maximum aantal gebruiken bereikt.',
      promo_plan_not_eligible: 'Deze code geldt niet voor dit aanbod.',
      promo_not_for_annual: 'Deze code geldt niet bij jaarlijkse facturatie.',
      promo_too_many_tries: 'Te veel pogingen. Probeer over een minuut opnieuw.',
      promo_not_applied: 'Deze code kon niet op dit aanbod worden toegepast.',
      promo_unavailable: 'Controle momenteel onmogelijk. Probeer zo meteen opnieuw.',
      generic: 'Controle momenteel onmogelijk. Probeer zo meteen opnieuw.',
    },
  },
  en: {
    label: 'Promo code',
    placeholder: 'Your code',
    apply: 'Apply',
    applying: '…',
    remove: 'Remove',
    applied: 'Code {C} applied',
    was: 'instead of',
    errors: {
      promo_unknown: 'Invalid code.',
      promo_inactive: 'This code is no longer active.',
      promo_expired: 'This code has expired.',
      promo_exhausted: 'This code has reached its maximum number of uses.',
      promo_plan_not_eligible: 'This code does not apply to this offer.',
      promo_not_for_annual: 'This code is not valid on annual billing.',
      promo_too_many_tries: 'Too many attempts. Try again in a minute.',
      promo_not_applied: 'This code could not be applied to this offer.',
      promo_unavailable: 'Cannot check right now. Please try again in a moment.',
      generic: 'Cannot check right now. Please try again in a moment.',
    },
  },
};

/** Centimes → « 17,49 € ». Une seule règle de format pour les trois langues. */
export function formatCents(cents, currency = 'eur') {
  if (typeof cents !== 'number') return null;
  const symbol = String(currency).toLowerCase() === 'eur' ? '€' : String(currency).toUpperCase();
  const euros = Math.floor(cents / 100);
  const rest = cents % 100;
  return rest === 0
    ? `${euros} ${symbol}`
    : `${euros},${String(rest).padStart(2, '0')} ${symbol}`;
}

export default function PromoCodeField({
  // `starter` | `pro` | `letter_oneshot`
  plan,
  billingCycle = 'monthly',
  language = 'fr',
  disabled = false,
  // Répéter le prix barré ici n'a de sens que si l'écran d'accueil ne
  // l'affiche pas déjà. Dans les deux modales d'Archer, le récapitulatif
  // porte le montant : le champ se contente alors de confirmer le code.
  showPrice = true,
  // Remonte `null` (aucun code) ou l'objet renvoyé par le serveur.
  onApplied,
  // Code déjà saisi ailleurs dans le parcours — aujourd'hui celui du
  // formulaire d'inscription, que l'écran de paiement ne doit pas
  // redemander. Il est posé dans le champ ET validé au montage, comme
  // si l'utilisateur venait de cliquer « Appliquer » : un code affiché
  // mais non revalidé sur CETTE offre annoncerait une remise que le
  // checkout refuserait.
  initialCode = '',
  // Libellé de remplacement. Le formulaire d'inscription veut « Code
  // promo (optionnel) » : sans ce point d'entrée, il devait poser son
  // propre titre au-dessus et le champ en affichait deux.
  label = null,
  testId = 'promo',
}) {
  const t = COPY[String(language || 'fr').toLowerCase()] || COPY.fr;
  const [code, setCode] = useState(initialCode || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(null);

  // `apply` est recréé à chaque frappe (il dépend de `code`) : le garder
  // dans une ref permet aux effets ci-dessous de l'appeler sans se
  // relancer à chaque caractère tapé.
  const applyRef = useRef(null);

  const messageFor = useCallback((e) => {
    const detail = e?.response?.data?.detail;
    const key = (detail && typeof detail === 'object' && detail.error) || null;
    return t.errors[key] || t.errors.generic;
  }, [t]);

  const apply = useCallback(async (override) => {
    const trimmed = String(override ?? code).trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/promo-codes/validate`,
        { code: trimmed, plan, billing_cycle: billingCycle },
        // L'endpoint accepte désormais les appelants anonymes (le champ
        // vit aussi dans le formulaire d'inscription, où personne n'a
        // encore de session). On envoie quand même les cookies : sur un
        // parcours connecté, la limitation de débit compte alors par
        // compte plutôt que par IP.
        { withCredentials: true },
      );
      setApplied(data);
      if (onApplied) onApplied(data);
    } catch (e) {
      setApplied(null);
      if (onApplied) onApplied(null);
      setError(messageFor(e));
    } finally {
      setBusy(false);
    }
  }, [code, busy, plan, billingCycle, onApplied, messageFor]);

  applyRef.current = apply;

  // ── Changement d'offre ──────────────────────────────────────────────
  // Le code appliqué ne vaut plus : il a été validé CONTRE UNE OFFRE, et
  // ce n'est plus la même. Mais l'effacer en silence, comme on le
  // faisait, laissait l'utilisateur devant un champ vidé sans un mot —
  // il croit avoir mal cliqué, retape le même code, se le fait refuser,
  // et n'a toujours pas compris. On garde donc ce qu'il a saisi et on
  // REJOUE la validation : il lit « Ce code ne s'applique pas à cette
  // offre » à la seconde où il bascule sur Pro, ce qui est l'information
  // qu'il lui faut.
  //
  // Premier rendu exclu : c'est le montage, traité par l'effet suivant.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setApplied(null);
    setError(null);
    if (onApplied) onApplied(null);
    const pending = (code || '').trim();
    if (pending) applyRef.current(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, billingCycle]);

  // ── Code hérité du parcours ─────────────────────────────────────────
  // Une seule fois, au montage. Le champ affiche déjà le code (état
  // initial) ; cet effet le fait vérifier pour que le prix barré soit
  // celui du serveur et non une promesse reprise d'un écran précédent.
  useEffect(() => {
    const seed = (initialCode || '').trim();
    if (seed) applyRef.current(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = useCallback(() => {
    setApplied(null);
    setError(null);
    setCode('');
    if (onApplied) onApplied(null);
  }, [onApplied]);

  const locked = disabled || busy;

  return (
    <div data-testid={testId} style={{ marginTop: 12 }}>
      <label
        htmlFor={`${testId}-input`}
        style={{
          display: 'block', fontSize: 11, fontWeight: 800, color: '#6b7280',
          letterSpacing: '.06em', marginBottom: 6,
        }}
      >
        {(label || t.label).toUpperCase()}
      </label>

      {applied ? (
        <div
          data-testid={`${testId}-applied`}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '10px 12px', borderRadius: 10,
            background: '#f0fdf4', border: '1px solid #bbf7d0',
          }}
        >
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#15803d', minWidth: 0 }}>
            {t.applied.replace('{C}', applied.code)}
          </span>
          {showPrice && typeof applied.final_amount === 'number' && (
            <span style={{ fontSize: 12.5, color: '#166534', whiteSpace: 'nowrap' }}>
              <s style={{ color: '#6b7280' }}>{formatCents(applied.base_amount, applied.currency)}</s>
              {' → '}
              <strong data-testid={`${testId}-new-price`}>
                {formatCents(applied.final_amount, applied.currency)}
              </strong>
            </span>
          )}
          <button
            type="button"
            onClick={remove}
            disabled={locked}
            data-testid={`${testId}-remove`}
            style={{
              marginLeft: 'auto', background: 'transparent', border: 'none',
              color: '#15803d', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              textDecoration: 'underline', cursor: locked ? 'not-allowed' : 'pointer',
              padding: 0, minHeight: 24,
            }}
          >
            {t.remove}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            id={`${testId}-input`}
            data-testid={`${testId}-input`}
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(null); }}
            // Entrée applique le code — et n'envoie surtout pas le
            // formulaire de paiement qui l'entoure.
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
            placeholder={t.placeholder}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            disabled={locked}
            style={{
              flex: '1 1 140px', minWidth: 0, minHeight: 42, padding: '10px 12px',
              border: `1px solid ${error ? '#fca5a5' : '#d1d5db'}`, borderRadius: 10,
              fontSize: 14, fontFamily: 'inherit', color: '#0a0a0f',
              textTransform: 'uppercase', background: locked ? '#f9fafb' : '#fff',
            }}
          />
          <button
            type="button"
            // `() => apply()` et non `apply` : React passerait l'événement
            // du clic en premier argument, que `apply` prendrait pour un
            // code à valider.
            onClick={() => apply()}
            disabled={locked || !code.trim()}
            data-testid={`${testId}-apply`}
            style={{
              minHeight: 42, padding: '10px 16px', borderRadius: 10,
              background: '#fff', color: '#0a0a0f', border: '1px solid #d1d5db',
              fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit',
              cursor: (locked || !code.trim()) ? 'not-allowed' : 'pointer',
              opacity: (locked || !code.trim()) ? 0.55 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {busy ? t.applying : t.apply}
          </button>
        </div>
      )}

      {error && (
        <div
          data-testid={`${testId}-error`}
          role="alert"
          style={{ marginTop: 7, fontSize: 12, color: '#991b1b', lineHeight: 1.45 }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
